import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import StoreHeader from "../components/store/StoreHeader";
import SmartFiltersPanel from "../components/store/SmartFiltersPanel";
import Footer from "../components/store/Footer";
import StoreSplash from "../components/store/StoreSplash";
import { StoreLoadingProvider } from "../context/StoreLoadingContext";
import "./StoreLayout.css";

function StoreLayoutContent() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  /**
   * Los filtros tambien se pueden pedir por la url: /store?category=5
   *
   * Hace falta para que el "See all parts" del carrusel de relacionados
   * caiga en el catalogo ya filtrado por la categoria de la pieza. De paso,
   * un catalogo filtrado ahora se puede compartir por link.
   *
   * Solo al entrar o al cambiar el parametro: despues manda el panel de
   * filtros, si no elegir a mano se pisaria con el valor de la url.
   */
  const categoriaUrl = searchParams.get("category");
  const marcaUrl = searchParams.get("brand");

  useEffect(() => {
    if (categoriaUrl !== null) {
      const n = Number(categoriaUrl);
      setCategory(Number.isFinite(n) && n > 0 ? n : null);
    }
  }, [categoriaUrl]);

  useEffect(() => {
    if (marcaUrl !== null) {
      const n = Number(marcaUrl);
      setBrand(Number.isFinite(n) && n > 0 ? n : null);
    }
  }, [marcaUrl]);

  const isProductDetail = location.pathname.startsWith("/store/product/");
  const isCart = location.pathname.startsWith("/store/cart");
  const isCheckout = location.pathname.startsWith("/store/checkout");
  const hideFilters = isProductDetail || isCheckout || isCart;

  const normalizeId = useCallback((v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }, []);

  const hasActive = useMemo(() => Boolean(category || brand), [category, brand]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setShowFilters(false);
  }, [location.pathname]);

  useEffect(() => {
    const scroller =
      document.querySelector(".store-layout") ||
      document.querySelector(".store-container");

    if (scroller) scroller.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    if (hideFilters) setShowFilters(false);
  }, [hideFilters]);

  useEffect(() => {
    if (!isMobile && showFilters) setShowFilters(false);
  }, [isMobile, showFilters]);

  return (
  <div className="store-scope">
    <StoreSplash />

    <div className="store-layout">
      <StoreHeader onSearch={setSearch} />

      {!hideFilters && isMobile && (
        <button
          className="store-mobile-filter-btn"
          onClick={() => setShowFilters(true)}
        >
          <span className="store-mobile-filter-ic" aria-hidden="true">⎚</span>
          <span>Filters</span>
          {hasActive && (
            <span className="store-mobile-filter-badge" aria-hidden="true" />
          )}
        </button>
      )}

      <div className={`store-content ${hideFilters ? "no-filters" : ""}`}>
        {!hideFilters && !isMobile && (
          <div className="store-smart-filters">
            <SmartFiltersPanel
              category={category}
              brand={brand}
              onCategory={(id) => setCategory(normalizeId(id))}
              onBrand={(id) => setBrand(normalizeId(id))}
              onClear={() => {
                setCategory(null);
                setBrand(null);
              }}
              mobileOpen={false}
              setMobileOpen={() => {}}
            />
          </div>
        )}

        <main className="store-container">
          <Outlet context={{ search, category, brand }} />
        </main>
      </div>

      {!hideFilters && isMobile && (
        <SmartFiltersPanel
          category={category}
          brand={brand}
          onCategory={(id) => setCategory(normalizeId(id))}
          onBrand={(id) => setBrand(normalizeId(id))}
          onClear={() => {
            setCategory(null);
            setBrand(null);
          }}
          mobileOpen={showFilters}
          setMobileOpen={setShowFilters}
        />
      )}

      <Footer />
    </div>
  </div>
);
}

// El provider envuelve al layout para que la pantalla de carga pueda
// esperar a las peticiones que disparan los hijos.
function StoreLayout() {
  return (
    <StoreLoadingProvider>
      <StoreLayoutContent />
    </StoreLoadingProvider>
  );
}

export default StoreLayout;
